"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion } from "framer-motion";
import { useCart } from "@/hooks/use-cart";
import { useWishlist } from "@/hooks/use-wishlist";
import { Button } from "@/components/ui/button";
import { ShoppingCart, Eye, Heart } from "lucide-react";
import { toast } from "sonner";
import { createClient } from "@/lib/supabase/client";
import ProductQuickView from "./product-quick-view";
import { Star } from "lucide-react";

interface Product {
  id: number;
  name: string;
  price: number;
  slug: string;
  stock: number;
  description: string;
  image_url: string;
  has_variants: boolean;
  discount_percentage?: number;
  original_price?: number;
  discount_starts_at?: string | null;
  discount_ends_at?: string | null;
  is_on_sale?: boolean;
}

interface ProductCardProps {
  product: Product;
}

export default function ProductCard({ product }: ProductCardProps) {
  const { addItem } = useCart();
  const { addItem: addToWishlist, removeItem: removeFromWishlist, isInWishlist, init: initWishlist } = useWishlist();
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
  const [isInUserWishlist, setIsInUserWishlist] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [quantity, setQuantity] = useState(1);
  
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
    }).format(price); 
  };

  useEffect(() => {
    const checkAuth = async () => {
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();
      setIsAuthenticated(!!user);
      
      if (user) {
        await initWishlist();
      }
    };
    
    checkAuth();
  }, [initWishlist]);
  
  useEffect(() => {
    setIsInUserWishlist(isInWishlist(product.id));
  }, [isInWishlist, product.id]);
  
  const hasDiscount = product.discount_percentage !== undefined 
  && product.discount_percentage > 0 
  && product.original_price !== undefined 
  && product.original_price > 0
  && product.original_price > product.price;
  
  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    
    if (product.stock <= 0) {
      toast.error(`${product.name} is out of stock`);
      return;
    }
    
    // Add the selected quantity to cart
    for (let i = 0; i < quantity; i++) {
      addItem(product);
    }
    
    // Reset quantity after adding to cart
    setQuantity(1);
  };

  const openQuickView = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsQuickViewOpen(true);
  };
  
  const toggleWishlist = async (e: React.MouseEvent) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      toast.error("Please login to add items to your wishlist");
      return;
    }
    
    if (isInUserWishlist) {
      await removeFromWishlist(product.id);
    } else {
      await addToWishlist(product);
    }
  };

  
  
  return (
    <>
      <motion.div 
        whileHover={{ y: -5 }}
        className="group bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all border border-gray-100"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Link href={`/product/${product.slug}`}>
          <div className="relative aspect-square">
            <Image 
              src={product.image_url || '/placeholder-product.jpg'} 
              alt={product.name}
              fill
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
              className="object-contain rounded-t-lg transition-transform group-hover:scale-105 duration-500 ease-in-out p-2"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            
            {/* Wishlist button */}
            <Button
              variant="ghost" 
              size="icon"
              className="absolute top-2 right-2 rounded-full bg-white/70 hover:bg-white p-1.5 shadow-sm z-10"
              onClick={toggleWishlist}
              aria-label={isInUserWishlist ? "Remove from wishlist" : "Add to wishlist"}
            >
              <Heart className={`h-4 w-4 ${isInUserWishlist ? 'text-[#9A2643] fill-current' : 'text-gray-600'}`} />
            </Button>
            
            {/* Stock status */}
            {product.stock < 5 && product.stock > 0 && (
              <div className="absolute top-10 right-2 bg-orange-500 text-white text-xs px-2 py-1 rounded-full font-medium shadow-md z-10">
                Low Stock
              </div>
            )}
            {product.stock === 0 && (
              <div className="absolute top-10 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium shadow-md z-10">
                Out of Stock
              </div>
            )}
            
            {/* Discount badge */}
{hasDiscount && (
  <div className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded z-10">
    {Math.round(product.discount_percentage || 0)}% OFF
  </div>
)}
          </div>
          
          <div className="p-4">
            {/* Star rating */}
            <div className="flex items-center mb-1">
              {[...Array(5)].map((_, i) => (
                <Star 
                  key={i} 
                  className={`h-3.5 w-3.5 ${i < 4 ? 'text-[#F2C94C] fill-current' : 'text-gray-300'}`} 
                />
              ))}
              <span className="text-gray-500 text-xs ml-1">4.8</span>
            </div>
            
            <h3 className="font-medium text-base line-clamp-1 text-gray-800">{product.name}</h3>
            
            {/* Price section */}
<div className="flex items-center justify-between mt-1">
  <div className="flex items-center gap-2">
    {hasDiscount ? (
      // Show discounted price with original price
      <>
        <p className="text-lg font-bold text-gray-900">
          {formatPrice(product.price)}
        </p>
        <span className="text-sm text-gray-500 line-through">
          {formatPrice(product.original_price || 0)}
        </span>
      </>
    ) : (
      // Show regular price
      <p className="text-lg font-bold text-gray-900">
        {formatPrice(product.price)}
      </p>
    )}
  </div>
  
  {product.has_variants && (
    <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
      Options
    </span>
  )}
</div>
            
<div className="mt-3 flex flex-col sm:flex-row gap-2 sm:gap-3">
  {product.stock > 0 && (
    <div className="flex items-center border border-gray-200 rounded-full overflow-hidden bg-white shadow-sm w-fit">
      <button 
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (quantity > 1) setQuantity(quantity - 1);
        }}
        className={`px-3 py-1.5 sm:px-3 sm:py-1.5 text-gray-600 hover:bg-gray-50 transition-colors ${
          quantity <= 1 ? 'opacity-40 cursor-not-allowed' : 'hover:text-[#9A2643]'
        }`}
        disabled={quantity <= 1}
        aria-label="Decrease quantity"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
        </svg>
      </button>
      <span className="px-2 py-1.5 text-sm font-medium w-8 text-center text-gray-700">
        {quantity}
      </span>
      <button 
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (quantity < product.stock) setQuantity(quantity + 1);
        }}
        className={`px-3 py-1.5 sm:px-3 sm:py-1.5 text-gray-600 hover:bg-gray-50 transition-colors ${
          quantity >= product.stock ? 'opacity-40 cursor-not-allowed' : 'hover:text-[#9A2643]'
        }`}
        disabled={quantity >= product.stock}
        aria-label="Increase quantity"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      </button>
    </div>
  )}
  
  <button 
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      handleAddToCart(e);
    }}
    disabled={product.stock <= 0}
    className={`flex items-center justify-center rounded-full p-2.5 transition-all duration-200 transform hover:scale-105 ${
      product.stock > 0 
        ? 'bg-[#9A2643] hover:bg-[#7a1c35] text-white shadow-md hover:shadow-lg' 
        : 'bg-gray-200 text-gray-500 cursor-not-allowed'
    }`}
    aria-label={product.stock <= 0 ? 'Out of Stock' : 'Add to Cart'}
    title={product.stock <= 0 ? 'Out of Stock' : 'Add to Cart'}
  >
    <ShoppingCart size={18} className="flex-shrink-0" />
  </button>
</div>
          </div>
        </Link>
      </motion.div>
      
      <ProductQuickView 
        product={product}
        open={isQuickViewOpen}
        onClose={() => setIsQuickViewOpen(false)}
      />
    </>
  );
}